import PropTypes from 'prop-types';
import React from 'react';
import { Modal, Button, Select, Form, Section, Date, TimeInput, DropdownLight, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { useForm } from '../../../utils/validators';
import timezones from './../../../timezones/timezones.json';

import Styles from '../../../styles/rate/modalSettingsEvent.module.css';
import moment from 'moment';
import { getHoursFromDate, getDayFromDate, transformDateStringUtcIntoDate } from '../../../utils/date';
import i18n from 'i18next';

export default function ModalSettingsEvent({ event, organization, settings, showModal, onSuccess, onCloseModal }) {
    const { t } = useTranslation();
    const locale = i18n.language;
    const toast = React.useContext(ToastContext);

    const validateDate = React.useCallback((name, date, dateForm) => {
        let tmpStart = dateForm.start_sale || null;
        let tmpEnd = dateForm.end_sale || null;
        if (tmpStart && tmpEnd && tmpStart.isAfter(tmpEnd)) {
            return t('ticket.rates.modalSettingsEvent.startDateBeforeEndDate');
        }
        if (tmpStart && tmpEnd && +tmpStart === +tmpEnd) {
            return t('ticket.rates.modalSettingsEvent.sameDates');
        }
        return true;
    }, []);

    const [formSettings, setFormSettings, setFormSettingsObject, formErrors, settingsValidate] = useForm(
        {
            start_sale: null,
            end_sale: null
        },
        {
            start_sale: validateDate,
            end_sale: validateDate
        }
    );

    let timezonesOptions = React.useMemo(() => {
        return timezones.map(v => ({ key: v, label: v, value: v }));
    }, [timezones]);

    React.useEffect(() => {
        const end_sale = settings.end_sale ? transformDateStringUtcIntoDate(settings.end_sale, event) : null;
        const start_sale = settings.start_sale ? transformDateStringUtcIntoDate(settings.start_sale, event) : null;

        setFormSettingsObject({
            end_sale: end_sale,
            start_sale: start_sale
        });
    }, [settings, event]);

    React.useEffect(() => {
        // This useEffect is here to validate form on each changment. Do not include settingsValidate to avoid infinit loops
        settingsValidate();
    }, [formSettings]);

    const changeDate = React.useCallback(
        (field, dateString) => {
            if (dateString === null) {
                // avoid clear with keyboard
                return;
            }

            const date = dateString.split('-');
            let oldDate = moment.utc(formSettings[field]).tz(event.timezone);
            oldDate.set('year', date[0]);
            // on set month, jan = 0, dec = 11
            oldDate.set('month', date[1] - 1);
            oldDate.set('date', date[2]);
            setFormSettings(field, oldDate);
        },
        [formSettings, event]
    );

    const changeHours = React.useCallback(
        (field, timeString) => {
            let _timeString = timeString;
            if (!timeString || timeString === '') {
                // avoid clear with keyboard
                _timeString = '12:00';
            }

            const date = _timeString.split(':');
            let oldDate = moment.utc(formSettings[field]).tz(event.timezone);
            oldDate.set('hour', date[0]);
            oldDate.set('minute', date[1]);
            setFormSettings(field, oldDate);
        },
        [formSettings, setFormSettings, event]
    );

    const handleSubmit = React.useCallback(() => {
        if (!settingsValidate()) {
            return;
        }

        settings.start_sale = formSettings.start_sale ? moment.utc(formSettings.start_sale).format('YYYY-MM-DDTHH:mm:ss') : null;
        settings.end_sale = formSettings.end_sale ? moment.utc(formSettings.end_sale).format('YYYY-MM-DDTHH:mm:ss') : null;

        settings
            .save({
                organizationId: organization.id,
                eventId: event.id
            })
            .then(() => {
                onSuccess();
                onCloseModal();
                toast.success(t('ticket.rates.modalSettingsEvent.success'));
            })
            .catch(e => {
                toast.error(t('ticket.rates.modalSettingsEvent.error'));
            });
    }, [formSettings, settingsValidate, onCloseModal, event, organization]);

    return (
        <Modal
            open={showModal}
            size={'large'}
            onClose={() => {
                onCloseModal();
            }}
            title={t('ticket.rates.modalSettingsEvent.title')}
            subtitle={t('ticket.rates.modalSettingsEvent.subtitle')}
            allowExternalClosing={false}
            submitButton={<Button primary onClick={handleSubmit} label={t('ticket.common.action.save')} disabled={Object.keys(formErrors).length > 0} />}
            cancelButton={<Button onClick={() => onCloseModal()} label={t(`ticket.common.action.cancel`)} />}
        >
            <Form>
                <Form.Field className={Styles['field-selector']}>
                    <Form.Label>{t(`ticket.rates.modalSettingsEvent.startSales`)}</Form.Label>
                    <Select
                        errors={formErrors['start_sale'] ? true : false}
                        onChange={value => {
                            if (value === 1) {
                                setFormSettingsObject({ start_sale: null }, true);
                            } else {
                                // NOW
                                let now = moment.utc().tz(event.timezone);
                                setFormSettingsObject({ start_sale: now }, true);
                            }
                        }}
                        options={[
                            { text: t(`ticket.rates.modalSettingsEvent.startOnPublish`), value: 1 },
                            { text: t(`ticket.rates.modalSettingsEvent.startOnSpecificDate`), value: 2 }
                        ]}
                        value={formSettings.start_sale ? 2 : 1}
                    />
                </Form.Field>

                {formSettings.start_sale && (
                    <Section className={Styles['modal-settings-event-section']}>
                        <div className={Styles['date-container']}>
                            <Form.Field className={Styles['date-form-field']}>
                                <Form.Label>{t('ticket.common.date.start')}</Form.Label>
                                <Date.Input
                                    className={Styles['input-start-date']}
                                    errors={Boolean(formErrors['start_sale'])}
                                    value={getDayFromDate(formSettings.start_sale, event)}
                                    locale={locale}
                                    onChange={date => {
                                        changeDate('start_sale', date);
                                    }}
                                    minYear={2010}
                                    maxYear={2050}
                                    returnFormat={'YYYY-MM-DD'}
                                />
                            </Form.Field>
                            <Form.Field>
                                <Form.Label>{t('ticket.common.time.start')}</Form.Label>
                                <TimeInput
                                    errors={Boolean(formErrors['start_sale'])}
                                    onChange={time => changeHours('start_sale', time)}
                                    value={getHoursFromDate(formSettings.start_sale, event)}
                                    step={15}
                                />
                            </Form.Field>
                            <Form.Field>
                                <DropdownLight className={Styles['timezone']} value={event.timezone} items={timezonesOptions} disabled />
                            </Form.Field>
                        </div>
                        {formErrors['start_sale'] && <Form.FieldErrors errors={[formErrors['start_sale'] || null]} />}
                    </Section>
                )}

                <Form.Field className={Styles['field-selector']}>
                    <Form.Label>{t(`ticket.rates.modalSettingsEvent.endSales`)}</Form.Label>
                    <Select
                        errors={formErrors['end_sale'] ? true : false}
                        onChange={value => {
                            let now = moment.utc().tz(event.timezone);
                            let _end_date = now.add(1, 'hours');
                            if (value === 1) {
                                setFormSettingsObject({ end_sale: null }, true);
                            } else {
                                // NOW
                                if (formSettings.start_sale) {
                                    let start_sale = moment.utc(formSettings.start_sale).tz(event.timezone);
                                    _end_date = start_sale.add(1, 'hours');
                                }
                                setFormSettingsObject({ end_sale: _end_date }, true);
                            }
                        }}
                        options={[
                            { text: t(`ticket.rates.modalSettingsEvent.endOnPublish`), value: 1 },
                            { text: t(`ticket.rates.modalSettingsEvent.endOnSpecificDate`), value: 2 }
                        ]}
                        value={formSettings.end_sale ? 2 : 1}
                    />
                </Form.Field>

                {formSettings.end_sale && (
                    <Section className={Styles['modal-settings-event-section']}>
                        <div className={Styles['date-container']}>
                            <Form.Field className={Styles['date-form-field']}>
                                <Form.Label>{t('ticket.common.date.end')}</Form.Label>
                                <Date.Input
                                    className={Styles['input-end-date']}
                                    errors={Boolean(formErrors['end_sale'])}
                                    value={getDayFromDate(formSettings.end_sale, event)}
                                    locale={locale}
                                    onChange={date => {
                                        changeDate('end_sale', date);
                                    }}
                                    minYear={2010}
                                    maxYear={2050}
                                    returnFormat={'YYYY-MM-DD'}
                                />
                            </Form.Field>
                            <Form.Field>
                                <Form.Label>{t('ticket.common.time.end')}</Form.Label>
                                <TimeInput
                                    errors={Boolean(formErrors['end_sale'])}
                                    onChange={time => changeHours('end_sale', time)}
                                    value={getHoursFromDate(formSettings.end_sale, event)}
                                    step={15}
                                />
                            </Form.Field>
                            <Form.Field>
                                <DropdownLight className={Styles['timezone']} value={event.timezone} items={timezonesOptions} disabled />
                            </Form.Field>
                        </div>
                        {formErrors['end_sale'] && <Form.FieldErrors errors={[formErrors['end_sale'] || null]} />}
                    </Section>
                )}
            </Form>
        </Modal>
    );
}

ModalSettingsEvent.propTypes = {
    event: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired,
    settings: PropTypes.object.isRequired,
    showModal: PropTypes.bool.isRequired,
    onCloseModal: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired
};
